import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import {
  attachFileToChinaShipment,
  getChinaShipmentByContainerNo,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import {
  ActivityIndicator,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  convertXlsx,
  DapiConvertResponse,
  DapiFilmLogResponse,
  DapiShipmentItem,
  isFilmLog,
} from "../../../services/dapiService";
import { pushFileToS3 } from "../../../services/s3BucketService";
import { useTranslation } from "../../../utils/useTranslation";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

interface Props {
  visible: boolean;
  onClose: () => void;
  // A film log whose 装柜清单 is in the same batch cannot be attached yet — that
  // shipment does not exist until the batch is submitted — so it travels with the
  // handoff and is filed afterwards.
  onNext: (data: DapiConvertResponse[], pendingFilmLogs: DapiFilmLogResponse[]) => void;
}

type Step = "upload" | "loading" | "review";

// One packing list = one container = one shipment, so a batch is capped low enough
// that the single-replica DAPI pod can convert them one after another.
const MAX_FILES = 5;

type FileStatus = {
  name: string;
  status: "pending" | "converting" | "done" | "failed";
  error?: string;
};

const str = (v: any): string => (v == null ? "" : String(v));
const num = (v: any): string => {
  const n = Number(v);
  return v == null || v === "" || Number.isNaN(n) ? "—" : n.toLocaleString();
};

// Quantity drawn from existing stock ("走库存") rather than an order. doc_splits is
// authoritative; items parsed before it existed fall back to a single split flagged
// by from_stock — same rule the MS No match modal applies.
const stockQtyForItem = (item: DapiShipmentItem): number => {
  const splits = item.doc_splits?.length
    ? item.doc_splits
    : [{ type: item.from_stock ? "stock" : "order", qty: item.total_qty }];
  return splits.reduce(
    (sum, s) => (s.type === "stock" ? sum + (Number(s.qty) || 0) : sum),
    0,
  );
};

const totalStockQty = (items: DapiShipmentItem[]): number =>
  items.reduce((sum, item) => sum + stockQtyForItem(item), 0);

// ─── Theme (matches ChinaShipmentDetailsReport.tsx) ───────────────────────────
const C = {
  card: "#FFFFFF",
  border: "#DDE1E8",
  borderLight: "#ECEEF2",
  navy: "#1A2B4A",
  navyLight: "#2C3E6B",
  accent: "#2563EB",
  accentLight: "#EEF3FD",
  label: "#64748B",
  value: "#1E293B",
  divider: "#E8EBF0",
  muted: "#94A3B8",
};

const ChinaShipmentUploadModal: React.FC<Props> = ({ visible, onClose, onNext }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const toast = useToast();
  const [nextError, setNextError] = useState<string>("");
  const fileInputRef = useRef<any>(null);
  const pendingFilmLogs = useRef<DapiFilmLogResponse[]>([]);
  const [step, setStep] = useState<Step>("upload");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);
  const [error, setError] = useState<string>("");
  // `data` is the container currently being reviewed; `results` holds the whole
  // converted batch, with edits written back on navigate/next.
  const [results, setResults] = useState<DapiConvertResponse[]>([]);
  const [reviewIdx, setReviewIdx] = useState<number>(0);
  const [data, setData] = useState<DapiConvertResponse | null>(null);
  // Which items are folded away. Tracked as collapsed rather than expanded so the
  // empty default is "all open", and an item from the next container in the batch
  // opens with the rest instead of inheriting a set of indices from the last one.
  const [collapsedItems, setCollapsedItems] = useState<Set<number>>(new Set());

  const settledCount = fileStatuses.filter(
    (fs) => fs.status === "done" || fs.status === "failed",
  ).length;
  const progressPct = fileStatuses.length
    ? (settledCount / fileStatuses.length) * 100
    : 0;

  const handleFileChange = (e: any) => {
    const files: File[] = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > MAX_FILES) {
      setSelectedFiles([]);
      setError(`${t("tooManyFilesError")} ${MAX_FILES}`);
      return;
    }
    setSelectedFiles(files);
    setError("");
  };

  // Converts one file at a time: DAPI runs a single small pod, and a failure on one
  // packing list must not cost the user the ones that already parsed.
  const handleConvert = async () => {
    if (!selectedFiles.length) {
      setError(t("selectFileFirstError"));
      return;
    }
    setStep("loading");
    setError("");
    setFileStatuses(selectedFiles.map((f) => ({ name: f.name, status: "pending" })));
    pendingFilmLogs.current = [];

    const markStatus = (idx: number, patch: Partial<FileStatus>) =>
      setFileStatuses((prev) =>
        prev.map((fs, i) => (i === idx ? { ...fs, ...patch } : fs)),
      );

    const converted: DapiConvertResponse[] = [];
    // A 胶片装柜明细 is not a shipment — it details a film line its own packing list
    // already states, so it is filed against that container instead of converted.
    const filmLogs: DapiFilmLogResponse[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      markStatus(i, { status: "converting" });
      try {
        const base64 = await fileToBase64(file);
        const s3Result = await pushFileToS3(
          "POST",
          base64,
          file.name,
          file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "china_shipments"
        );
        if (!s3Result?.signedUrl) {
          throw new Error(t("uploadToS3FailedError"));
        }
        // Ask for the 外箱标 pictures: the matching screen shows them so the
        // operator can see which customer the factory printed for, which is the one
        // thing that separates two runs sharing an item code.
        const result = await convertXlsx(s3Result.signedUrl, true);
        if (isFilmLog(result)) {
          filmLogs.push(result);
        } else {
          converted.push(result);
        }
        markStatus(i, { status: "done" });
      } catch (err: any) {
        markStatus(i, {
          status: "failed",
          error: err?.message || t("failedToProcessFileError"),
        });
      }
    }

    // Filed against a container that already has a shipment, or whose 装柜清单 is in
    // this same batch and about to be created. The upload has already put the file
    // in S3, so a rejected film log simply leaves it there unreferenced.
    const filmLogErrors: string[] = [];
    for (const log of filmLogs) {
      const container = String(log.container_no || "").trim();
      if (!container) {
        filmLogErrors.push(t("filmLogNoContainerError").replace("{file}", log.file_name));
        continue;
      }
      const inThisBatch = converted.some(
        (c) => String(c.container_no || "").toUpperCase() === container.toUpperCase(),
      );
      let exists = inThisBatch;
      if (!exists) {
        try {
          const found = await dispatch(getChinaShipmentByContainerNo(container)).unwrap();
          exists = !!found?.exists;
        } catch {
          exists = false;
        }
      }
      if (!exists) {
        filmLogErrors.push(t("filmLogNoShipmentError").replace("{container}", container));
        continue;
      }
      // A shipment created from this same batch does not exist yet, so its film log
      // is attached once the batch has been submitted rather than now.
      if (inThisBatch) {
        pendingFilmLogs.current.push(log);
        continue;
      }
      try {
        await dispatch(
          attachFileToChinaShipment({ containerNo: container, fileName: log.file_name }),
        ).unwrap();
      } catch {
        filmLogErrors.push(t("filmLogAttachFailedError").replace("{file}", log.file_name));
      }
    }

    // Nothing to review — the batch was film logs, or everything failed.
    if (!converted.length) {
      if (filmLogErrors.length) {
        setError(filmLogErrors.join("\n"));
        setStep("upload");
        return;
      }
      if (filmLogs.length) {
        toast.show(t("filmLogAttachedSuccess"), { type: "success" });
        onClose();
        return;
      }
      setError(t("allFilesFailedError"));
      setStep("upload");
      return;
    }
    if (filmLogErrors.length) {
      setError(filmLogErrors.join("\n"));
    } else if (filmLogs.length) {
      toast.show(t("filmLogAttachedSuccess"), { type: "success" });
    }

    setResults(converted);
    setReviewIdx(0);
    setData(converted[0]);
    setCollapsedItems(new Set());
    setStep("review");
  };

  // Review edits live on `data`; fold them back into the batch before it is read.
  const commitCurrent = (): DapiConvertResponse[] =>
    data ? results.map((r, i) => (i === reviewIdx ? data : r)) : results;

  const goToContainer = (nextIdx: number) => {
    if (nextIdx < 0 || nextIdx >= results.length) return;
    const merged = commitCurrent();
    setResults(merged);
    setReviewIdx(nextIdx);
    setData(merged[nextIdx]);
    setCollapsedItems(new Set());
    setNextError("");
  };

  const handleClose = () => {
    setStep("upload");
    setSelectedFiles([]);
    setFileStatuses([]);
    setError("");
    setResults([]);
    setReviewIdx(0);
    setData(null);
    setNextError("");
    setCollapsedItems(new Set());
    onClose();
  };

  const handleNext = () => {
    const merged = commitCurrent();

    const missingIdx = merged.findIndex((r) => !r.container_no);
    if (missingIdx !== -1) {
      goToContainer(missingIdx);
      setNextError(t("containerNoRequiredForMatch"));
      return;
    }

    // Two packing lists for the same container in one batch would have the second
    // silently replace the first — make the user resolve it instead.
    const seen = new Set<string>();
    for (const r of merged) {
      const cn = String(r.container_no).trim().toUpperCase();
      if (seen.has(cn)) {
        setNextError(`${t("duplicateContainerInBatchError")} ${r.container_no}`);
        return;
      }
      seen.add(cn);
    }

    setNextError("");
    onNext(merged, pendingFilmLogs.current);
    handleClose();
  };

  const updateHeader = (field: keyof DapiConvertResponse, value: string) => {
    setData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const updateItem = (index: number, field: keyof DapiShipmentItem, value: string) => {
    setData((prev) => {
      if (!prev) return prev;
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const toggleItem = (index: number) => {
    setCollapsedItems((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const expandAllItems = () => setCollapsedItems(new Set());
  const collapseAllItems = () =>
    setCollapsedItems(new Set((data?.items ?? []).map((_: any, i: number) => i)));

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={[styles.modal, step === "review" && styles.modalLarge]}>
          {/* ── Title bar ── */}
          <View style={styles.titleRow}>
            <Text style={styles.title}>
              {step === "review" ? t("reviewShipmentData") : t("uploadShipmentFile")}
            </Text>
            <TouchableOpacity onPress={handleClose} style={styles.closeIcon}>
              <MaterialCommunityIcons name="close" size={20} color="#555" />
            </TouchableOpacity>
          </View>

          {/* ── Upload step ── */}
          {step === "upload" && (
            <View style={styles.uploadArea}>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                multiple
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <TouchableOpacity
                style={styles.dropZone}
                onPress={() => fileInputRef.current?.click()}
                activeOpacity={0.7}
              >
                <MaterialCommunityIcons name="file-excel" size={48} color="#21a750" />
                <Text style={styles.dropZoneText}>
                  {selectedFiles.length
                    ? `${selectedFiles.length} ${t("filesSelectedLabel")}`
                    : t("clickToSelectXlsx")}
                </Text>
                <Text style={styles.dropZoneSub}>
                  {t("maxFilesHint")} {MAX_FILES}
                </Text>
              </TouchableOpacity>

              {selectedFiles.length > 0 && (
                <View style={styles.fileStatusList}>
                  {selectedFiles.map((f, i) => (
                    <View key={`${f.name}-${i}`} style={styles.fileStatusRow}>
                      <MaterialCommunityIcons
                        name="file-excel-outline"
                        size={16}
                        color="#21a750"
                      />
                      <Text style={styles.fileStatusName} numberOfLines={1}>
                        {f.name}
                      </Text>
                      <Text style={styles.fileStatusMeta}>
                        {(f.size / 1024).toFixed(1)} KB
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {error !== "" && <Text style={styles.errorText}>{error}</Text>}

              <View style={styles.uploadFooter}>
                <TouchableOpacity style={styles.cancelBtn} onPress={handleClose}>
                  <Text style={styles.cancelBtnText}>{t("cancel")}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.convertBtn, !selectedFiles.length && styles.btnDisabled]}
                  onPress={handleConvert}
                  disabled={!selectedFiles.length}
                >
                  <Text style={styles.convertBtnText}>{t("convertBtn")}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* ── Loading step ── */}
          {step === "loading" && (
            <View style={styles.loadingArea}>
              <ActivityIndicator size="large" color="#4dabf7" />
              <Text style={styles.loadingText}>
                {t("processingFile")} {settledCount}/{fileStatuses.length}
              </Text>

              <View style={styles.progressTrack}>
                <View style={[styles.progressFill, { width: `${progressPct}%` }]} />
              </View>

              <View style={styles.fileStatusList}>
                {fileStatuses.map((fs, i) => (
                  <View key={`${fs.name}-${i}`} style={styles.fileStatusRow}>
                    {fs.status === "converting" ? (
                      <ActivityIndicator size="small" color="#4dabf7" />
                    ) : (
                      <MaterialCommunityIcons
                        name={
                          fs.status === "done"
                            ? "check-circle"
                            : fs.status === "failed"
                              ? "alert-circle"
                              : "circle-outline"
                        }
                        size={16}
                        color={
                          fs.status === "done"
                            ? "#21a750"
                            : fs.status === "failed"
                              ? "#e03131"
                              : "#adb5bd"
                        }
                      />
                    )}
                    <Text style={styles.fileStatusName} numberOfLines={1}>
                      {fs.name}
                    </Text>
                    {fs.status === "failed" && (
                      <Text style={styles.fileStatusErrorText} numberOfLines={1}>
                        {fs.error}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* ── Review step ── */}
          {step === "review" && data && (
            <>
              {/* Container navigator - only meaningful for a multi-file batch */}
              {results.length > 1 && (
                <View style={styles.containerNav}>
                  <TouchableOpacity
                    style={[
                      styles.containerNavBtn,
                      reviewIdx === 0 && styles.containerNavBtnDisabled,
                    ]}
                    onPress={() => goToContainer(reviewIdx - 1)}
                    disabled={reviewIdx === 0}
                  >
                    <MaterialCommunityIcons name="chevron-left" size={18} color={C.navy} />
                  </TouchableOpacity>

                  <Text style={styles.containerNavLabel}>
                    {t("containerProgressLabel")} {reviewIdx + 1} / {results.length}
                    {data.container_no ? `  ·  ${data.container_no}` : ""}
                  </Text>

                  <TouchableOpacity
                    style={[
                      styles.containerNavBtn,
                      reviewIdx === results.length - 1 && styles.containerNavBtnDisabled,
                    ]}
                    onPress={() => goToContainer(reviewIdx + 1)}
                    disabled={reviewIdx === results.length - 1}
                  >
                    <MaterialCommunityIcons name="chevron-right" size={18} color={C.navy} />
                  </TouchableOpacity>
                </View>
              )}

              <ScrollView style={styles.reviewScroll} showsVerticalScrollIndicator={false}>
                {/* Source file banner */}
                <View style={styles.sourceBanner}>
                  <MaterialCommunityIcons name="file-check" size={16} color="#21a750" />
                  <Text style={styles.sourceBannerText}>{data.source_file}</Text>
                </View>

                {/* ── Stat strip ── */}
                <View style={styles.statsStrip}>
                  <View style={styles.statItem}>
                    <Text style={styles.statValue}>{data.items.length}</Text>
                    <Text style={styles.statLabel}>{t("items")}</Text>
                  </View>
                  <View style={styles.statDivider} />
                  <View style={styles.statItem}>
                    <Text style={styles.statValue}>{num(data.container_total_boxes)}</Text>
                    <Text style={styles.statLabel}>{t("totalBoxesLabel")}</Text>
                  </View>
                  <View style={styles.statDivider} />
                  <View style={styles.statItem}>
                    <Text style={styles.statValue}>{num(data.container_total_gross_kg)}</Text>
                    <Text style={styles.statLabel}>{t("totalGrossKgLabel")}</Text>
                  </View>
                  <View style={styles.statDivider} />
                  <View style={styles.statItem}>
                    <Text style={styles.statValue}>{num(data.container_total_net_kg)}</Text>
                    <Text style={styles.statLabel}>{t("totalNetKgLabel")}</Text>
                  </View>
                  <View style={styles.statDivider} />
                  <View style={styles.statItem}>
                    <Text
                      style={[
                        styles.statValue,
                        totalStockQty(data.items) > 0 && styles.statValueStock,
                      ]}
                    >
                      {num(totalStockQty(data.items))}
                    </Text>
                    <Text style={styles.statLabel}>{t("stockQtyStatLabel")}</Text>
                  </View>
                </View>

                {/* ── Subtotals by category (合计) ── */}
                {/* The packing list states one subtotal per product category and they
                    are not addable — 11 板 plus 378 箱 is not 389 of anything — so each
                    is shown as the sheet states it rather than rolled into one figure. */}
                {data.subtotals?.length > 0 && (
                  <View style={styles.subtotalBox}>
                    <Text style={styles.subtotalHeading}>{t("subtotalsHeading")}</Text>
                    {data.subtotals.map((s, i) => (
                      <View key={i} style={styles.subtotalRow}>
                        <Text style={styles.subtotalLabel} numberOfLines={1}>
                          {s.label}
                        </Text>
                        <Text style={styles.subtotalCount}>
                          {s.count != null ? `${num(s.count)} ${s.unit ?? ""}`.trim() : "—"}
                        </Text>
                        <Text style={styles.subtotalWeight}>
                          {s.gross_kg != null ? `${num(s.gross_kg)} kg` : "—"}
                          <Text style={styles.subtotalWeightTag}> {t("subtotalGrossShort")}</Text>
                        </Text>
                        <Text style={styles.subtotalWeight}>
                          {s.net_kg != null ? `${num(s.net_kg)} kg` : "—"}
                          <Text style={styles.subtotalWeightTag}> {t("subtotalNetShort")}</Text>
                        </Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* ── Container header ── */}
                <SectionHeader title={t("containerDetails")} />
                <View style={styles.containerCard}>
                  <View style={styles.metaGrid}>
                    <View style={styles.metaCol}>
                      <EditableField label={t("containerNoExcel")} value={str(data.container_no)}
                        onChange={(v) => updateHeader("container_no", v)} />
                      <EditableField label={t("vesselVoyageExcel")} value={str(data.vessel)}
                        onChange={(v) => updateHeader("vessel", v)} />
                      <EditableField label={t("shippingLine")} value={str(data.shipping_line)}
                        onChange={(v) => updateHeader("shipping_line", v)} />
                      <EditableField label={t("freight")} value={str(data.freight)}
                        onChange={(v) => updateHeader("freight", v)} />
                      <EditableField label={t("forwarder")} value={str(data.forwarder)}
                        onChange={(v) => updateHeader("forwarder", v)} />
                    </View>
                    <View style={styles.metaDivider} />
                    <View style={styles.metaCol}>
                      <EditableField label={t("containerType")} value={str(data.container_type)}
                        onChange={(v) => updateHeader("container_type", v)} />
                      <EditableField label={t("sealNoExcel")} value={str(data.seal)}
                        onChange={(v) => updateHeader("seal", v)} />
                      <EditableField label={t("departureDate")} value={str(data.departure_date)}
                        onChange={(v) => updateHeader("departure_date", v)} />
                      <EditableField label={t("arrivalDate")} value={str(data.arrival_date)}
                        onChange={(v) => updateHeader("arrival_date", v)} />
                      <EditableField label={t("destination")} value={str(data.destination)}
                        onChange={(v) => updateHeader("destination", v)} />
                    </View>
                  </View>

                  <View style={styles.itemDivider} />

                  <View style={styles.metaGrid}>
                    <View style={styles.metaCol}>
                      <EditableField label={t("freeDays")} value={str(data.free_days)}
                        onChange={(v) => updateHeader("free_days", v)} keyboardType="numeric" />
                      <EditableField label={t("vgmKg")} value={str(data.vgm_kg)}
                        onChange={(v) => updateHeader("vgm_kg", v)} keyboardType="numeric" noBorder />
                    </View>
                    <View style={styles.metaDivider} />
                    <View style={styles.metaCol}>
                      <EditableField label={t("responsiblePerson")} value={str(data.responsible_person)}
                        onChange={(v) => updateHeader("responsible_person", v)} />
                      <EditableField label={t("reportDate")} value={str(data.report_date)}
                        onChange={(v) => updateHeader("report_date", v)} noBorder />
                    </View>
                  </View>

                  <View style={styles.itemDivider} />

                  <View style={styles.remarksBlock}>
                    <Text style={styles.fieldLabel}>{t("remarksLabel")}</Text>
                    <TextInput
                      style={[styles.plainInput, styles.remarksInput]}
                      value={str(data.remarks)}
                      onChangeText={(v) => updateHeader("remarks", v)}
                      multiline
                      placeholder="—"
                      placeholderTextColor="#bbb"
                    />
                  </View>
                </View>

                {/* ── Items ── */}
                <SectionHeader
                  title={`${t("itemsLabel")} (${data.items.length})`}
                  right={
                    data.items.length > 1 ? (
                      <View style={styles.foldControls}>
                        <TouchableOpacity onPress={expandAllItems} style={styles.foldBtn}>
                          <MaterialCommunityIcons name="unfold-more-horizontal" size={14} color={C.muted} />
                          <Text style={styles.foldBtnText}>{t("expandAllBtn")}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={collapseAllItems} style={styles.foldBtn}>
                          <MaterialCommunityIcons name="unfold-less-horizontal" size={14} color={C.muted} />
                          <Text style={styles.foldBtnText}>{t("collapseAllBtn")}</Text>
                        </TouchableOpacity>
                      </View>
                    ) : undefined
                  }
                />

                {data.items.map((item, idx) => {
                  const isExpanded = !collapsedItems.has(idx);
                  const title = str(item.cn_item_name) || `Item ${idx + 1}`;
                  // The factory's 外箱标, straight from the workbook. This used to
                  // probe S3 for the CN 商品代码 under the label prefix, which is keyed
                  // on SAP's PicturName — so every lookup missed and every card drew
                  // the placeholder, at one request per item.
                  const labelImage = item.label_images?.[0]?.data;

                  return (
                    <View key={idx} style={styles.itemCard}>
                      {/* Item header */}
                      <TouchableOpacity
                        style={styles.itemCardHeader}
                        onPress={() => toggleItem(idx)}
                        activeOpacity={0.7}
                      >
                        <View style={styles.itemIndexBadge}>
                          <Text style={styles.itemIndexText}>
                            {String(item.seq ?? idx + 1).padStart(2, "0")}
                          </Text>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.itemTitle} numberOfLines={1}>{title}</Text>
                          {item.cn_item_code ? (
                            <Text style={styles.itemSubtitle} numberOfLines={1}>{item.cn_item_code}</Text>
                          ) : null}
                        </View>
                        {item.item_type ? (
                          <View style={styles.badge}>
                            <Text style={styles.badgeText}>{item.item_type}</Text>
                          </View>
                        ) : null}
                        <MaterialCommunityIcons
                          name={isExpanded ? "chevron-up" : "chevron-down"}
                          size={18}
                          color="#666"
                          style={{ marginLeft: 8 }}
                        />
                      </TouchableOpacity>

                      {isExpanded && (
                        <View style={styles.itemBody}>
                          {/* Meta grid: CN name/code/material | MS No/feature/spec */}
                          <View style={styles.metaGrid}>
                            <View style={styles.metaCol}>
                              <EditableField label={t("cnItemNameLabel")} value={str(item.cn_item_name)}
                                onChange={(v) => updateItem(idx, "cn_item_name", v)} />
                              <EditableField label={t("cnItemCodeLabel")} value={str(item.cn_item_code)}
                                onChange={(v) => updateItem(idx, "cn_item_code", v)} />
                              <EditableField label={t("materialExcel")} value={str(item.material)}
                                onChange={(v) => updateItem(idx, "material", v)} noBorder />
                            </View>
                            <View style={styles.metaDivider} />
                            <View style={styles.metaCol}>
                              <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{t("msRef")}</Text>
                                <Text style={styles.readOnlyValue}>
                                  {item.doc_nos?.length ? item.doc_nos.join(", ") : "—"}
                                </Text>
                              </View>
                              {stockQtyForItem(item) > 0 && (
                                <View style={styles.fieldRow}>
                                  <Text style={styles.fieldLabel}>{t("stockQtyStatLabel")}</Text>
                                  <Text style={[styles.readOnlyValue, styles.readOnlyValueStock]}>
                                    {num(stockQtyForItem(item))}
                                  </Text>
                                </View>
                              )}
                              <EditableField label={t("featureExcel")} value={str(item.feature)}
                                onChange={(v) => updateItem(idx, "feature", v)} />
                              <EditableField label={t("specExcel")} value={str(item.spec)}
                                onChange={(v) => updateItem(idx, "spec", v)} noBorder />
                            </View>
                          </View>

                          <View style={styles.itemDivider} />

                          {/* Quantity / Gross / Net formula rows + image */}
                          <View style={styles.qtyAndImage}>
                            <View style={styles.qtyRows}>
                              <QtyFormulaRow
                                label={t("quantity")}
                                unitLabel={t("unitPcs")}
                                ctnsLabel={t("ctns")}
                                perUnitValue={str(item.pcs_per_box)}
                                cartonsValue={str(item.boxes)}
                                totalValue={str(item.total_qty)}
                                onChangePerUnit={(v) => updateItem(idx, "pcs_per_box", v)}
                                onChangeCartons={(v) => updateItem(idx, "boxes", v)}
                                onChangeTotal={(v) => updateItem(idx, "total_qty", v)}
                              />
                              <QtyFormulaRow
                                label={t("grossWeight")}
                                unitLabel={t("unitKg")}
                                ctnsLabel={t("ctns")}
                                perUnitValue={str(item.gross_per_box)}
                                cartonsValue={str(item.boxes)}
                                totalValue={str(item.total_gross_kg)}
                                onChangePerUnit={(v) => updateItem(idx, "gross_per_box", v)}
                                onChangeCartons={(v) => updateItem(idx, "boxes", v)}
                                onChangeTotal={(v) => updateItem(idx, "total_gross_kg", v)}
                              />
                              <QtyFormulaRow
                                label={t("netWeight")}
                                unitLabel={t("unitKg")}
                                ctnsLabel={t("ctns")}
                                perUnitValue={str(item.net_per_box)}
                                cartonsValue={str(item.boxes)}
                                totalValue={str(item.total_net_kg)}
                                onChangePerUnit={(v) => updateItem(idx, "net_per_box", v)}
                                onChangeCartons={(v) => updateItem(idx, "boxes", v)}
                                onChangeTotal={(v) => updateItem(idx, "total_net_kg", v)}
                              />
                            </View>

                            <View style={styles.imageContainer}>
                              {labelImage ? (
                                <Image
                                  source={{ uri: labelImage }}
                                  style={styles.productImage}
                                  resizeMode="contain"
                                />
                              ) : (
                                <View style={styles.imagePlaceholder}>
                                  <MaterialCommunityIcons name="image-off-outline" size={22} color={C.muted} />
                                  <Text style={styles.imagePlaceholderText}>{t("noImage")}</Text>
                                </View>
                              )}
                            </View>
                          </View>

                          <View style={styles.itemDivider} />

                          {/* Secondary weights */}
                          <View style={styles.fieldRowCompact}>
                            <FieldInput label={t("palletWtLabel")} value={str(item.pallet_weight)}
                              onChange={(v) => updateItem(idx, "pallet_weight", v)} keyboardType="numeric" />
                            <FieldInput label={t("paperCoreWtLabel")} value={str(item.paper_core_weight)}
                              onChange={(v) => updateItem(idx, "paper_core_weight", v)} keyboardType="numeric" />
                            <FieldInput label={t("actualGrossKgLabel")} value={str(item.actual_gross_kg)}
                              onChange={(v) => updateItem(idx, "actual_gross_kg", v)} keyboardType="numeric" />
                          </View>

                          {/* Outer carton mark */}
                          <View style={styles.remarksBlock}>
                            <Text style={styles.fieldLabel}>{t("outerCartonMarkLabel")}</Text>
                            <TextInput
                              style={styles.plainInput}
                              value={str(item.outer_carton_mark)}
                              onChangeText={(v) => updateItem(idx, "outer_carton_mark", v)}
                              placeholder="—"
                              placeholderTextColor="#bbb"
                            />
                          </View>

                          {/* Remarks */}
                          <View style={styles.remarksBlock}>
                            <Text style={styles.fieldLabel}>{t("remarksLabel")}</Text>
                            <TextInput
                              style={[styles.plainInput, styles.remarksInput]}
                              value={str(item.remarks)}
                              onChangeText={(v) => updateItem(idx, "remarks", v)}
                              multiline
                              placeholder="—"
                              placeholderTextColor="#bbb"
                            />
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}

                <View style={{ height: 24 }} />
              </ScrollView>

              {/* ── Review footer ── */}
              {nextError !== "" && <Text style={styles.errorText}>{nextError}</Text>}
              <View style={styles.reviewFooter}>
                <TouchableOpacity style={styles.cancelBtn} onPress={handleClose}>
                  <Text style={styles.cancelBtnText}>{t("cancel")}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.backBtn}
                  onPress={() => setStep("upload")}
                >
                  <Text style={styles.backBtnText}>← {t("backBtn")}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.convertBtn} onPress={handleNext}>
                  <Text style={styles.convertBtnText}>{t("nextBtn")}</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

// ─── Reusable sub-components ──────────────────────────────────────────────────
const SectionHeader = ({
  title,
  right,
}: {
  title: string;
  right?: React.ReactNode;
}) => (
  <View style={styles.sectionHeaderRow}>
    <View style={styles.sectionHeaderBar} />
    <Text style={styles.sectionHeaderText}>{title}</Text>
    {right ? <View style={styles.sectionHeaderRight}>{right}</View> : null}
  </View>
);

const EditableField = ({
  label,
  value,
  onChange,
  keyboardType = "default",
  noBorder = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  keyboardType?: any;
  noBorder?: boolean;
}) => (
  <View style={[styles.fieldRow, noBorder && { borderBottomWidth: 0 }]}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <TextInput
      style={styles.rowInput}
      value={value}
      onChangeText={onChange}
      keyboardType={keyboardType}
      placeholder="—"
      placeholderTextColor="#bbb"
    />
  </View>
);

const FieldInput = ({
  label,
  value,
  onChange,
  keyboardType = "default",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  keyboardType?: any;
}) => (
  <View style={styles.compactFieldCell}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <TextInput
      style={styles.plainInput}
      value={value}
      onChangeText={onChange}
      keyboardType={keyboardType}
      placeholder="—"
      placeholderTextColor="#bbb"
    />
  </View>
);

const QtyFormulaRow = ({
  label,
  unitLabel,
  ctnsLabel,
  perUnitValue,
  cartonsValue,
  totalValue,
  onChangePerUnit,
  onChangeCartons,
  onChangeTotal,
}: {
  label: string;
  unitLabel: string;
  ctnsLabel: string;
  perUnitValue: string;
  cartonsValue: string;
  totalValue: string;
  onChangePerUnit: (v: string) => void;
  onChangeCartons: (v: string) => void;
  onChangeTotal: (v: string) => void;
}) => (
  <View style={styles.qtyRow}>
    <Text style={styles.qtyLabel}>{label}</Text>
    <View style={styles.qtyFormula}>
      <TextInput style={styles.qtyBox} keyboardType="numeric" value={perUnitValue}
        onChangeText={onChangePerUnit} placeholder="—" placeholderTextColor="#bbb" />
      <Text style={styles.qtyUnit}>{unitLabel}/{ctnsLabel}</Text>
      <Text style={styles.qtyOp}>×</Text>
      <TextInput style={styles.qtyBox} keyboardType="numeric" value={cartonsValue}
        onChangeText={onChangeCartons} placeholder="—" placeholderTextColor="#bbb" />
      <Text style={styles.qtyUnit}>{ctnsLabel}</Text>
      <Text style={styles.qtyOp}>=</Text>
      <TextInput style={[styles.qtyBox, styles.qtyBoxResult]} keyboardType="numeric" value={totalValue}
        onChangeText={onChangeTotal} placeholder="—" placeholderTextColor="#bbb" />
      <Text style={[styles.qtyUnit, styles.qtyUnitAccent]}>{unitLabel}</Text>
    </View>
  </View>
);

export default ChinaShipmentUploadModal;

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.35)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    width: "50%",
    maxHeight: "80%",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 12,
  },
  modalLarge: {
    width: "94%",
    maxHeight: "94%",
  },

  // ── Title ──
  titleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 17,
    fontWeight: "700",
    color: "#111",
  },
  closeIcon: {
    padding: 4,
  },

  // ── Upload step ──
  uploadArea: {
    gap: 16,
  },
  dropZone: {
    borderWidth: 2,
    borderColor: "#d0e8d0",
    borderStyle: "dashed",
    borderRadius: 12,
    paddingVertical: 40,
    alignItems: "center",
    gap: 10,
    backgroundColor: "#f6fbf6",
  },
  dropZoneText: {
    fontSize: 15,
    color: "#333",
    fontWeight: "500",
    textAlign: "center",
  },
  dropZoneSub: {
    fontSize: 12,
    color: "#888",
  },
  errorText: {
    fontSize: 13,
    color: "#e03131",
  },
  uploadFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 8,
  },

  // ── Loading step ──
  loadingArea: {
    paddingVertical: 48,
    alignItems: "center",
    gap: 14,
  },
  loadingText: {
    fontSize: 14,
    color: "#555",
  },
  progressTrack: {
    width: "70%",
    height: 6,
    borderRadius: 3,
    backgroundColor: C.borderLight,
    overflow: "hidden",
  },
  progressFill: {
    height: 6,
    borderRadius: 3,
    backgroundColor: "#4dabf7",
  },

  // ── Batch file list (upload + loading steps) ──
  fileStatusList: {
    width: "100%",
    gap: 6,
    marginTop: 4,
  },
  fileStatusRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: "#F8FAFC",
  },
  fileStatusName: {
    flex: 1,
    fontSize: 12,
    color: C.value,
  },
  fileStatusMeta: {
    fontSize: 11,
    color: C.muted,
  },
  fileStatusErrorText: {
    flexShrink: 1,
    fontSize: 11,
    color: "#e03131",
  },

  // ── Container navigator (review step) ──
  containerNav: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: C.accentLight,
    marginBottom: 10,
  },
  containerNavLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: C.navy,
  },
  containerNavBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: C.border,
  },
  containerNavBtnDisabled: {
    opacity: 0.4,
  },

  // ── Buttons ──
  cancelBtn: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 7,
    paddingHorizontal: 20,
    paddingVertical: 9,
    backgroundColor: "#fff",
  },
  cancelBtnText: {
    fontSize: 14,
    color: "#444",
    fontWeight: "500",
  },
  convertBtn: {
    backgroundColor: "#4dabf7",
    borderRadius: 7,
    paddingHorizontal: 28,
    paddingVertical: 9,
  },
  convertBtnText: {
    fontSize: 14,
    color: "#fff",
    fontWeight: "700",
  },
  btnDisabled: {
    backgroundColor: "#a5d8ff",
  },
  backBtn: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 7,
    paddingHorizontal: 20,
    paddingVertical: 9,
    backgroundColor: "#fff",
  },
  backBtnText: {
    fontSize: 14,
    color: "#444",
    fontWeight: "500",
  },

  // ── Review ──
  reviewScroll: {
    flex: 1,
  },
  sourceBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#f0fbf2",
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: "#c3e6cb",
  },
  sourceBannerText: {
    fontSize: 13,
    color: "#2f7a3f",
    fontWeight: "500",
  },

  // ── Section headers ──
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    marginTop: 4,
  },
  // Pushed to the far end of the header row, so the fold controls sit opposite the
  // title rather than crowding it.
  sectionHeaderRight: {
    marginLeft: "auto",
  },
  foldControls: {
    flexDirection: "row",
    gap: 6,
  },
  foldBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    backgroundColor: "#F8FAFC",
  },
  foldBtnText: {
    fontSize: 11,
    color: "#475569",
  },
  sectionHeaderBar: {
    width: 3,
    height: 14,
    borderRadius: 2,
    backgroundColor: C.accent,
  },
  sectionHeaderText: {
    fontSize: 13,
    fontWeight: "700",
    color: C.navy,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  // ── Stat strip ──
  statsStrip: {
    backgroundColor: C.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.border,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  subtotalBox: {
    backgroundColor: "#f8fafc",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 10,
  },
  subtotalHeading: {
    fontSize: 11,
    fontWeight: "700",
    color: "#475569",
    marginBottom: 6,
  },
  subtotalRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 3,
  },
  subtotalLabel: {
    flex: 1.4,
    fontSize: 12,
    fontWeight: "600",
    color: "#0f172a",
  },
  subtotalCount: {
    flex: 1,
    fontSize: 12,
    fontWeight: "700",
    color: "#21a750",
    textAlign: "right",
  },
  subtotalWeight: {
    flex: 1.3,
    fontSize: 12,
    color: "#334155",
    textAlign: "right",
  },
  subtotalWeightTag: {
    fontSize: 10,
    color: "#94a3b8",
  },
  statItem: { flex: 1, alignItems: "center" },
  statValue: { fontSize: 15, fontWeight: "700", color: C.navy },
  // 走库存 qty is drawn from stock rather than produced — highlight when present.
  statValueStock: { color: "#b45309" },
  statLabel: {
    fontSize: 10,
    color: C.muted,
    fontWeight: "500",
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  statDivider: { width: 1, height: 28, backgroundColor: C.divider },

  // ── Container / item cards ──
  containerCard: {
    backgroundColor: C.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.border,
    padding: 16,
    marginBottom: 16,
  },
  metaGrid: { flexDirection: "row" },
  metaCol: { flex: 1 },
  metaDivider: { width: 1, backgroundColor: C.divider, marginHorizontal: 14 },
  itemDivider: { height: 1, backgroundColor: C.divider, marginVertical: 12 },

  fieldRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.borderLight,
    gap: 10,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: C.label,
    flexShrink: 0,
  },
  rowInput: {
    flex: 1,
    maxWidth: "60%",
    height: 30,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    backgroundColor: "#F8FAFC",
    paddingHorizontal: 8,
    fontSize: 12,
    color: C.value,
    textAlign: "right",
  },
  readOnlyValueStock: {
    color: "#b45309",
    fontWeight: "700",
  },
  readOnlyValue: {
    fontSize: 12,
    fontWeight: "500",
    color: C.value,
    textAlign: "right",
    flexShrink: 1,
  },
  plainInput: {
    height: 32,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    backgroundColor: "#F8FAFC",
    paddingHorizontal: 8,
    fontSize: 12,
    color: C.value,
    marginTop: 4,
  },
  remarksInput: {
    height: 56,
    textAlignVertical: "top",
    paddingTop: 6,
  },
  remarksBlock: {
    marginTop: 4,
  },

  itemCard: {
    backgroundColor: C.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.border,
    marginBottom: 8,
    overflow: "hidden",
  },
  itemCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: "#F8FAFC",
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  itemIndexBadge: {
    width: 28,
    height: 28,
    borderRadius: 7,
    backgroundColor: C.accentLight,
    justifyContent: "center",
    alignItems: "center",
  },
  itemIndexText: { fontSize: 11, fontWeight: "800", color: C.accent },
  itemTitle: { fontSize: 13, fontWeight: "700", color: C.navy },
  itemSubtitle: { fontSize: 11, color: C.muted, marginTop: 1 },
  badge: {
    backgroundColor: C.navyLight,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 5,
  },
  badgeText: { fontSize: 10, fontWeight: "700", color: "#fff", letterSpacing: 0.3 },
  itemBody: {
    padding: 14,
  },

  // ── Compact secondary fields (pallet/paper-core/actual gross) ──
  fieldRowCompact: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 4,
  },
  compactFieldCell: {
    flex: 1,
  },

  // ── Qty formula rows ──
  qtyAndImage: { flexDirection: "row", alignItems: "flex-start", gap: 12 },
  qtyRows: { flex: 1, gap: 8 },
  qtyRow: { gap: 4 },
  qtyLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: C.label,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  qtyFormula: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  qtyBox: {
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 54,
    textAlign: "center",
    fontSize: 12,
    fontWeight: "600",
    color: C.value,
    backgroundColor: "#F8FAFC",
  },
  qtyBoxResult: {
    borderColor: C.accent,
    backgroundColor: C.accentLight,
    color: C.accent,
  },
  qtyUnit: { fontSize: 10, color: C.muted, fontWeight: "500" },
  qtyUnitAccent: { color: C.accent, fontWeight: "600" },
  qtyOp: { fontSize: 12, fontWeight: "700", color: C.label },

  // ── Item image ──
  imageContainer: {
    width: 100,
    height: 100,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.border,
    backgroundColor: "#F8FAFC",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  productImage: { width: "100%", height: "100%" },
  imagePlaceholder: { justifyContent: "center", alignItems: "center", gap: 4 },
  imagePlaceholderText: { fontSize: 9, color: C.muted, fontWeight: "500" },

  // ── Review footer ──
  reviewFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    marginTop: 4,
  },
});
